/**
 * Resolves task group config IDs (e.g., "tg-4-3-001") to display names
 * (e.g., "Daily Care & Operations").
 *
 * Uses the same ListTaskGroups query as useTaskGroupList — cached by Apollo.
 * Returns a resolver function compatible with Tabulator group header formatter.
 *
 * DRR-178: Scope dimension display resolution.
 */

import { useMemo } from 'react';
import { useTaskGroupList } from './useTaskGroups';

/**
 * Build a lookup map from task group configId → display name.
 * Returns a resolver function: (configId: string) => string
 *
 * Usage:
 *   const resolve = useResolveTaskGroupName();
 *   resolve("tg-4-3-001") // → "Daily Care & Operations"
 *   resolve("tg-unknown")  // → "tg-unknown" (passthrough)
 */
export function useResolveTaskGroupName(): (configId: string | null | undefined) => string {
  const { taskGroups } = useTaskGroupList();

  const lookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const tg of taskGroups) {
      if (tg.configId && tg.name) {
        map.set(tg.configId, tg.name);
      }
    }
    return map;
  }, [taskGroups]);

  return (configId: string | null | undefined): string => {
    if (!configId) return '(no group)';
    return lookup.get(configId) ?? configId;
  };
}
